import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import {
  EReferralProgramType,
  IParsedDataResponse,
  IReferralPayoutModel,
  IReferralProgramSettings,
  REFERRAL_PROGRAM_TYPE,
} from '../../../services/data/data-service.interface';
import { ConditionTypes } from '../../../helpers/preset-condition.interface';
import { getComponent, getSignsDataPql } from '../../../helpers/preset-condition.helper';
import { IValue } from '@platform500services/p500-ui-kit';
import { IReferralProgramModelsForm } from './types';
import { DestroySubscribers } from '../../../decorators/destroy-subscribers.decorator';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MODAL_TOOLTIP_KEY_ENUM } from '../controls-with-hint/hints';

@Component({
  selector: 'gtd-referral-program-conditions',
  templateUrl: './referral-program-conditions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReferralProgramConditionsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ReferralProgramConditionsComponent),
      multi: true,
    },
  ],
})
@DestroySubscribers()
export class ReferralProgramConditionsComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() public globalData: IParsedDataResponse;

  public models: IReferralPayoutModel;
  public formGroup = new FormGroup({
    models: new FormArray([
      new FormGroup<IReferralProgramModelsForm>({
        referralPayoutModel: new FormControl(null, Validators.required),
        payoutModelValue: new FormControl(null, Validators.required),
        conditions: new FormArray<FormControl<IValue>>([]),
      }),
    ]),
    program_type: new FormControl<EReferralProgramType>(EReferralProgramType.REFERRAL_BONUS),
  });

  public modalTooltipEnum = MODAL_TOOLTIP_KEY_ENUM;
  public programTypeOptions = REFERRAL_PROGRAM_TYPE;
  public onChange: any = () => {};
  public onTouched: any = () => {};

  conditions = [
    { key: 'country', label: $localize`:@@common.country:Country`, type: 'country' },
    { key: 'goalTypeUUID', label: $localize`:@@common.goal_type:Goal Type`, type: 'goal-type' },
    { key: 'affiliateTransactionUUType', label: $localize`:@@common.transaction_type:Transaction Type`, type: 'transaction-types' },
    { key: 'offerUUID', label: $localize`:@@common.offer:Offer`, type: 'offers-name' },
  ];

  public presetHelpers = {
    getValueComponentByType: (formControl: UntypedFormControl, key: string, type: ConditionTypes = '', label: string = '') => {
      return getComponent(formControl, key, type, label, this.globalData);
    },
    getSignsDataPql,
  };

  public hiddenOptionsList: Observable<string[]>[] = [this.getHiddenOptions$(0)];

  private subs: Record<string, Subscription> = {};

  public ngOnInit(): void {
    this.models = this.globalData.referral_payout_models;
    this.subs.valueChanges = this.formGroup.valueChanges.subscribe((models) => {
      this.onChange(models);
    });
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public writeValue(settings: IReferralProgramSettings | null): void {
    settings?.models?.forEach((setting, index) => {
      if (index > 0) {
        this.addControl();
      }
      if (setting.conditions.length > 0) {
        setting.conditions.forEach(() => this.addConditions(index));
      }
      setTimeout(() => this.formGroup.controls.models.at(index).controls.conditions.patchValue(settings.models[index].conditions));
    });
    this.formGroup.controls.models.patchValue(settings?.models);
    this.formGroup.controls.program_type.setValue(settings?.program_type ?? EReferralProgramType.REFERRAL_BONUS);
  }

  public validate(): ValidationErrors | null {
    return this.getErrors(this.formGroup.controls.models);
  }

  public addControl(): void {
    const formGroup = new FormGroup<IReferralProgramModelsForm>({
      referralPayoutModel: new FormControl(null, Validators.required),
      payoutModelValue: new FormControl(null, Validators.required),
      conditions: new FormArray([]),
    });
    this.formGroup.controls.models.push(formGroup);
    this.hiddenOptionsList.push(this.getHiddenOptions$(this.formGroup.controls.models.length - 1));
  }

  public addConditions(index: number): void {
    const fg = this.formGroup.controls.models.at(index);
    fg.controls.conditions.push(
      new FormControl(
        {
          key: null,
          config: 'AND',
          operator: null,
          value: null,
        },
        this.conditionIsValid
      )
    );
  }

  public removeCondition(conditionIndex: number, modelIndex: number): void {
    this.formGroup.controls.models.at(modelIndex).controls.conditions.removeAt(conditionIndex);
  }

  public removeModel(index: number): void {
    this.formGroup.controls.models.removeAt(index);
  }

  private getErrors(formArray: FormArray<FormGroup<IReferralProgramModelsForm>>): ValidationErrors | null {
    if (formArray.invalid) {
      const fg = formArray.controls.find((control) => control.invalid);
      return (
        fg.controls.referralPayoutModel.errors || fg.controls.payoutModelValue.errors || this.getConditionsError(fg.controls.conditions)
      );
    } else {
      return null;
    }
  }

  private getConditionsError(conditionsArray: FormArray<FormControl<IValue>>): ValidationErrors | null {
    if (conditionsArray.invalid) {
      const control = conditionsArray.controls.find((control) => control.errors);
      if (control.errors.invalid) {
        return { required: true };
      } else {
        return control.errors;
      }
    } else {
      return null;
    }
  }

  private conditionIsValid(control: FormControl<IValue>): ValidationErrors | null {
    if (!control.value) {
      return { required: true };
    }
    return Object.values(control.value).some((value) => !value) ? { required: true } : null;
  }

  private getHiddenOptions$(index: number): Observable<string[]> {
    return this.formGroup.controls.models.at(index).controls.conditions.valueChanges.pipe(
      map((items: Record<'key', string>[]) => items.filter((item) => item && item.key).map(({ key }) => key)),
      shareReplay()
    );
  }
}
